import { ElementRef, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Offset } from '../models/offset.model';
import { DocumentConfigService } from './document-config.service';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {

    private static readonly fileInputId = 'select-file';
    private fileInputElement: HTMLInputElement;
    private selectFilePromiseRejector: any;
    private readonly domParser: DOMParser = new DOMParser();

    constructor(private readonly documentConfigService: DocumentConfigService, private readonly domSanitizer: DomSanitizer) {
    }

    public addScriptToPage(url: string, onSuccess?: () => void, onError?: () => void, prepend: boolean = false, scriptId?: string, crossOrigin: boolean = false) {
        const doc = this.getDocument();
        if (!doc) {
            return;
        }

        if (this.findScript(scriptId)) {
            if (onSuccess) {
                onSuccess();
            }
            return;
        }

        const script: HTMLScriptElement = doc.createElement('script');
        script.src = url;
        script.id = scriptId;
        if (onSuccess) {
            script.onload = onSuccess;
        }
        if (onError) {
            script.onerror = onError;
        }
        if (prepend) {
            const firstScript = doc.getElementsByTagName('script')[0];
            if (firstScript && firstScript.parentNode) {
                firstScript.parentNode.insertBefore(script, firstScript);
                return;
            }
        }
        if (crossOrigin) {
            script.crossOrigin = 'anonymous';
        }

        doc.body.appendChild(script);
    }

    public addIframeToPage(url: string, id?: string) {
        if (!url && !id) {
            return;
        }

        const doc = this.getDocument();
        if (!doc || (id && doc.getElementById(id)) || doc.querySelector(`iframe[src="${url}"]`)) {
            return;
        }

        const iframe = doc.createElement('iframe');
        if (url) {
            iframe.setAttribute('src', url);
        }

        if (id) {
            iframe.id = id;
        }

        iframe.style.display = 'none';
        doc.body.appendChild(iframe);
    }

    // NOTE: If user hits 'cancel' in file dialog, the promise won't reject until a file dialog is opened again
    public selectFile(accept: Array<string>): Promise<FileList> {
        if (this.selectFilePromiseRejector) {
            this.selectFilePromiseRejector();
        }

        const doc = this.getDocument();
        if (this.fileInputElement) {
            doc.body.removeChild(this.fileInputElement);
        }

        const mockFileInfo = this.documentConfigService.getConfig().mockUploadFile;
        if (mockFileInfo) {
            const mockUploadFileList = this.createMockFileList(mockFileInfo.name, mockFileInfo.size);
            return Promise.resolve(mockUploadFileList);
        }

        this.fileInputElement = doc.createElement('input');
        this.fileInputElement.type = 'file';
        this.fileInputElement.id = DocumentService.fileInputId;
        this.fileInputElement.accept = accept.join(',');
        doc.body.appendChild(this.fileInputElement);

        return new Promise<FileList>((resolve, reject) => {
            this.selectFilePromiseRejector = reject;
            const handler = (event: any) => {
                const files = event.target.files as FileList;
                if (files && files.length) {
                    resolve(files);
                } else {
                    reject();
                }

                this.selectFilePromiseRejector = null;
                if (this.fileInputElement) {
                    this.fileInputElement.removeEventListener('change', handler);
                    doc.body.removeChild(this.fileInputElement);
                    this.fileInputElement = null;
                }
            };

            this.fileInputElement.addEventListener('change', handler);
            this.fileInputElement.click();
        });
    }

    public updateFavicon(url: string) {
        this.addOrUpdateLink(url, 'shortcut icon', 'image/x-icon', 'link[rel="shortcut icon"]');
    }

    public addOrUpdateStylesheet(url: string, id: string) {
        this.addOrUpdateLink(url, 'stylesheet', 'text/css', `#${id}`, id);
    }

    public addCssClassToElement(element: HTMLElement, cssClass: string) {
        if (!element) {
            return;
        }

        element.classList.add(cssClass);
    }

    public removeCssClassFromElement(element: HTMLElement, cssClass: string) {
        if (!element) {
            return;
        }

        element.classList.remove(cssClass);
    }

    public addOrUpdateStyleTag(id: string, styles: string) {
        const doc = this.getDocument();
        if (!doc) {
            return;
        }

        let tag = doc.getElementById(id);
        if (tag && tag.parentNode) {
            tag.parentNode.removeChild(tag);
        }

        tag = doc.createElement('style');
        tag.id = id;
        (tag as HTMLStyleElement).type = 'text/css';

        // Check for IE 10 stylesheet
        if ((tag as any).styleSheet) {
            (tag as any).styleSheet.cssText = styles;
        } else {
            tag.appendChild(doc.createTextNode(styles));
        }

        doc.head.appendChild(tag);
    }

    public addLinkTag(attributes: Map<string, string>) {
        const doc = this.getDocument();
        if (!doc) {
            return;
        }

        const linkTag = doc.createElement('link');
        attributes.forEach((value, key) => linkTag.setAttribute(key, value));
        doc.head.appendChild(linkTag);
    }

    // Make sure html is already properly encoded
    public appendHTMLToElement(element: HTMLElement, html: string) {
        if (!element || !html) {
            return;
        }

        const sanitizedHtmlString = this.domSanitizer.sanitize(1 /*HTML Security Context*/, html);
        const docToAppend = this.domParser.parseFromString(sanitizedHtmlString, 'text/html');
        if (docToAppend.documentElement.nodeName !== 'parsererror') {
            element.appendChild(docToAppend.body.firstChild);
        }
    }

    public getOffset(elementRef: ElementRef | Element): Offset {
        const element = elementRef instanceof ElementRef ? elementRef.nativeElement : elementRef;
        const rect = element.getBoundingClientRect();
        return {
            top: rect.top + this.nativeDocument.body.scrollTop,
            left: rect.left + this.nativeDocument.body.scrollLeft
        };
    }

    public disableTouchMove() {
        // Can't use the render2 listener because it doesn't support passive events
        this.getDocument().addEventListener('touchmove', this.preventDefault, { passive: false });
    }

    public enableTouchMove() {
        this.getDocument().removeEventListener('touchmove', this.preventDefault);
    }

    public updateThemeColor(color: string) {
        const doc = this.getDocument();
        if (!doc) {
            return;
        }

        const themeTag: HTMLMetaElement = doc.querySelector('meta[name="theme-color"]');
        if (themeTag) {
            if (themeTag.content !== color) {
                themeTag.content = color;
            }

            return;
        }

        const newThemeTag = doc.createElement('meta');
        newThemeTag.name = 'theme-color';
        newThemeTag.content = color;
        doc.head.appendChild(newThemeTag);
    }

    private preventDefault(event: Event): void {
        event.preventDefault();
    }

    public get nativeDocument(): Document {
        return this.getDocument();
    }

    private getDocument(): Document {
        return document;
    }

    private findScript(scriptId: string): boolean {
        if (!scriptId) {
            return false;
        }

        const doc = this.getDocument();
        if (!doc) {
            return false;
        }

        return !!doc.getElementById(scriptId);
    }

    private addOrUpdateLink(url: string, rel: string, type: string, selector: string, newId?: string) {
        if (!url) {
            return;
        }

        const doc = this.getDocument();
        if (!doc) {
            return;
        }

        let link: HTMLLinkElement = doc.querySelector(selector);
        if (link) {
            if (link.href !== url) {
                link.href = url;
            }

            return;
        }

        link = doc.createElement('link');
        link.rel = rel;
        link.type = type;
        link.href = url;
        if (newId) {
            link.id = newId;
        }
        doc.head.appendChild(link);
    }

    private createMockFileList(name: string, size: number): FileList {
        const blob = new Blob([new Uint8Array(size)], { type: '' });
        blob['name'] = name;
        return [blob] as any as FileList;
    }
}
